import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye, EyeOff, Send, PlusCircle, QrCode, Landmark, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { formatRupiah } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { TransactionRow } from "../components/TransactionRow";

const quickActions = [
  { to: "/transfer", label: "Transfer", icon: Send, color: "bg-blue-50 text-primary", testid: "quick-action-transfer" },
  { to: "/top-up", label: "Top Up", icon: PlusCircle, color: "bg-teal-50 text-[#0FA5A0]", testid: "quick-action-topup" },
  { to: "/qris", label: "QRIS", icon: QrCode, color: "bg-emerald-50 text-emerald-600", testid: "quick-action-qris" },
  { to: "/rekening", label: "Rekening", icon: Landmark, color: "bg-violet-50 text-violet-600", testid: "quick-action-rekening" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState(null);
  const [hideBalance, setHideBalance] = useState(false);

  useEffect(() => {
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => setWallet({ balance: 0 }));
    api.get("/transactions?limit=5").then((r) => setTxns(r.data)).catch(() => setTxns([]));
  }, []);

  const today = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <h1 className="font-heading text-2xl font-semibold" data-testid="dashboard-greeting">
          Halo, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-sm text-muted-foreground">{today}</p>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Saldo Card */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }}>
            <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(11,18,32,0.08)] overflow-hidden noise-overlay">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground font-medium">Saldo Quantum Pay</p>
                  <button
                    onClick={() => setHideBalance(!hideBalance)}
                    aria-label={hideBalance ? "Tampilkan saldo" : "Sembunyikan saldo"}
                    data-testid="saldo-card-visibility-toggle"
                    className="text-muted-foreground hover:text-foreground transition-[color] duration-200"
                  >
                    {hideBalance ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {wallet === null ? (
                  <Skeleton className="h-10 w-48 mb-4" />
                ) : (
                  <p className="font-heading text-3xl sm:text-4xl font-bold tabular-nums mb-4" data-testid="saldo-card-balance">
                    {hideBalance ? "Rp ••••••••" : formatRupiah(wallet.balance)}
                  </p>
                )}
                <div className="flex gap-3">
                  <Button className="bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] hover:opacity-90 text-white flex-1 sm:flex-none" onClick={() => navigate("/top-up")} data-testid="saldo-card-topup-button">
                    <PlusCircle className="h-4 w-4 mr-1.5" /> Top Up
                  </Button>
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary/5 flex-1 sm:flex-none" onClick={() => navigate("/transfer")} data-testid="saldo-card-transfer-button">
                    <Send className="h-4 w-4 mr-1.5" /> Transfer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick actions */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.12 }} className="grid grid-cols-4 gap-3">
            {quickActions.map((qa) => {
              const Icon = qa.icon;
              return (
                <Link
                  key={qa.to}
                  to={qa.to}
                  data-testid={qa.testid}
                  className="rounded-2xl border bg-white p-4 flex flex-col items-center gap-2 hover:shadow-[0_10px_30px_rgba(11,18,32,0.08)] hover:border-primary/30 active:scale-[0.98] transition-[box-shadow,border-color] duration-200"
                >
                  <span className={`h-11 w-11 rounded-full flex items-center justify-center ${qa.color}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-medium">{qa.label}</span>
                </Link>
              );
            })}
          </motion.div>
        </div>

        {/* Recent transactions */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.18 }} className="lg:col-span-1">
          <Card className="rounded-2xl h-full">
            <CardContent className="p-2">
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <h3 className="font-heading font-semibold">Transaksi Terbaru</h3>
                <Link to="/riwayat" className="text-xs text-primary font-medium flex items-center hover:underline" data-testid="dashboard-see-all-link">
                  Lihat Semua <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
              {txns === null ? (
                <div className="space-y-3 p-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
                </div>
              ) : txns.length === 0 ? (
                <div className="text-center py-10 px-4">
                  <div className="mx-auto h-14 w-14 rounded-full bg-secondary flex items-center justify-center mb-3">
                    <Send className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium">Belum ada transaksi</p>
                  <p className="text-xs text-muted-foreground mt-1">Top up saldo dan mulai transfer sekarang.</p>
                </div>
              ) : (
                <div className="divide-y divide-border/60">
                  {txns.map((t) => <TransactionRow key={t.id} txn={t} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
